import { useSavedDashboards } from "@/api/analytics";
import { usePermissions } from "@/hooks/usePermissions";

type TUseAnalyticsDashboardAccessOptions = {
  /** When set, also resolves whether this specific dashboard is accessible via share. */
  dashboardId?: string;
};

type TAnalyticsDashboardAccess = {
  isLoading: boolean;
  isError: boolean;
  hasModuleNavAccess: boolean;
  hasModuleListAccess: boolean;
  hasModuleDetailAccess: boolean;
  hasSharedDashboards: boolean;
  canShowAnalyticsNav: boolean;
  canAccessDashboardList: boolean;
  canAccessThisDashboard: boolean;
  canLoadDashboardContent: boolean;
};

/**
 * Combines Analytics module RBAC with resource-level dashboard shares.
 *
 * Module RBAC still gates create / explorer / admin. Users without those
 * permissions may access dashboards explicitly shared with them (BE list).
 */
export const useAnalyticsDashboardAccess = (
  options: TUseAnalyticsDashboardAccessOptions = {},
): TAnalyticsDashboardAccess => {
  const { dashboardId } = options;
  const { hasAnyPermission } = usePermissions();

  const hasModuleNavAccess = hasAnyPermission(["can-view-analytics"]);
  const hasModuleListAccess = hasAnyPermission([
    "can-view-analytics-dashboards",
  ]);
  const hasModuleDetailAccess = hasAnyPermission([
    "can-view-analytics-dashboard-details",
    "can-view-analytics-dashboards",
  ]);

  // Probe only when module RBAC cannot authorize list/detail on its own.
  const shouldProbeShares = !hasModuleListAccess || !hasModuleDetailAccess;

  const { data, isLoading, isError } = useSavedDashboards("", {
    enabled: shouldProbeShares,
    staleTime: 5 * 60 * 1000,
  });

  const accessibleDashboards = data?.data ?? [];
  const hasSharedDashboards = accessibleDashboards.some(
    (dashboard) => dashboard.ownership === "shared",
  );

  const isSharedDashboardAccessible = Boolean(
    dashboardId &&
    accessibleDashboards.some(
      (dashboard) =>
        dashboard.internal_id === dashboardId &&
        dashboard.ownership === "shared",
    ),
  );

  const canShowAnalyticsNav = hasModuleNavAccess || hasSharedDashboards;
  const canAccessDashboardList = hasModuleListAccess || hasSharedDashboards;
  const canAccessThisDashboard =
    hasModuleDetailAccess || isSharedDashboardAccessible;
  const canLoadDashboardContent =
    hasModuleListAccess || hasModuleDetailAccess || isSharedDashboardAccessible;

  return {
    isLoading: shouldProbeShares && isLoading,
    isError: shouldProbeShares && isError,
    hasModuleNavAccess,
    hasModuleListAccess,
    hasModuleDetailAccess,
    hasSharedDashboards,
    canShowAnalyticsNav,
    canAccessDashboardList,
    canAccessThisDashboard,
    canLoadDashboardContent,
  };
};
